import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router'
import { toast } from 'react-toastify'
import { useAPI } from '../../api/api'
import { LocationData } from '../../components/admin/LocationCard'
import { QrScannerExpanded } from '../../components/QRScannerExpanded'
import SplashScreen from '../../components/SplashScreen'
import './Deposit.css'
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-short-left.svg'
import { ReactComponent as ScanIcon } from '../../icons/qr_code.svg'
import { ReactComponent as ListIcon } from '../../icons/list.svg'
import { ReactComponent as BinIcon } from '../../icons/bin.svg'
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg'
import { ItemToAssignData } from '../merchant/AssignItems'
import moment from 'moment'
import i18n from '../../i18n'
import { Link } from 'react-router-dom'
import { parseItemQrCode } from '../../api/utils'

type DepositData = {
  magicToken: string
  dropoff: LocationData
  depositId: string
  supportCode: string
}

type ItemListData = {
  _id: string
  itemTypeIcon: string
  items: ItemToAssignData[]
}

type CategorizedDepositItems = {
  depositItems: ItemToAssignData[]
  assignedItems: ItemToAssignData[]
  otherItems: ItemToAssignData[]
}

const DepositItems = () => {
  let { t } = useTranslation(['translation', 'errors'])
  let location = useLocation()
  const passedData: DepositData = location.state as any as DepositData

  let { errors, wrappedExtendedQuery, wrappedExtendedMutate } = useAPI()

  const lang = i18n.language

  const [isLoading, setIsLoading] = useState(true)
  const [isScanning, setIsScanning] = useState('false')
  const [activeTab, setActiveTab] = useState(1)
  const [scannerActive, setScannerActive] = useState(true)
  const [scannedItems, setScannedItems] = useState<ItemToAssignData[]>([])
  const [itemListByType, setItemListByType] = useState<ItemListData[]>([])
  const [itemTypeExpanded, setItemTypeExpanded] = useState<ItemToAssignData[]>([])
  const [lastScannedItem, setLastScannedItem] = useState<ItemToAssignData | undefined>()
  const [currentCapacity, setCurrentCapacity] = useState(0)
  const [, setIsFull] = useState(false)
  const [supportCode, setSupportCode] = useState('')
  const [depositId, setDepositId] = useState('')
  const [, setMagicToken] = useState('')
  const [dropoff, setDropoff] = useState<LocationData | undefined>(undefined)

  const [showRemoveScanner, setShowRemoveScanner] = useState(false)
  const [showReturnOverview, setShowReturnOverview] = useState(false)

  const [categorizedDepositItems, setCategorizedDepositItems] = useState<CategorizedDepositItems>({ depositItems: [], assignedItems: [], otherItems: [] })

  const sleep = (ms: any) => new Promise(r => setTimeout(r, ms))

  let history = useHistory()

  const [ended, setEnded] = useState(false)

  useEffect(() => {
    async function onLoad() {
      setCurrentCapacity(passedData.dropoff.freeCapacity)
      setIsFull(passedData.dropoff.freeCapacity > 0 ? false : true)
      if (!!passedData && !!passedData.magicToken && !!passedData.supportCode && !!passedData.depositId && !!passedData.dropoff) {
        setSupportCode(passedData.supportCode)
        setDepositId(passedData.depositId)
        setMagicToken(passedData.magicToken)
        setDropoff(passedData.dropoff)
      } else {
        history.replace('/failed-deposit')
        return
      }
      await sleep(1000)
      setIsLoading(false)
    }
    onLoad().then(() => setIsLoading(false))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onQrCode = async (code: string) => {
    onQrCodeRead(code).then(() => setIsScanning('false'))
  }

  const onQrCodeRead = async (code: string) => {
    setLastScannedItem(undefined)

    const qr = parseItemQrCode(code)
    if (!qr) {
      toast.error(t('invalid-item-qr-code'))
      return
    }

    const query = `query { itemsByIdOpen (filter : {uuid : "${qr}"}) { _id uuid status serialNumber dueDate itemType { _id name iconUrl } merchant {name}}}`
    const factory = (result: any): ItemToAssignData => {
      return Array.isArray(result.itemsByIdOpen) && result.itemsByIdOpen.length > 0
        ? result.itemsByIdOpen.map((o: any) => ({
            id: o._id,
            serialNumber: o.serialNumber || null,
            label: o.itemType.name,
            itemTypeId: o.itemType._id,
            iconUrl: o.itemType.iconUrl,
            uuid: o.uuid,
            status: o.status,
            itemTypeName: o.itemType.name,
            merchant: o.merchant ? o.merchant.name : null,
            dueDate: o.dueDate || null,
          }))[0]
        : undefined
    }

    const item = await wrappedExtendedQuery(query, factory)

    if (!item) {
      toast.error(t('item-does-not-exist'))
      return
    }

    // Someone already claimed this container's deposit; tell the guest whether it was them and keep it off the list.
    // Read the ids from passedData: the scanner keeps the callback from its first render, so component state can be stale here
    if (item.status === 'deposit-returned') {
      const claimQuery = `query { depositItemClaimOpen(input: { depositId: "${passedData.depositId}", magicToken: "${passedData.magicToken}", itemId: "${item.id}" }) }`
      const claim = await wrappedExtendedQuery(claimQuery, (result: any) => result.depositItemClaimOpen)
      if (claim === 'claimed-by-you') {
        toast.warning(t('deposit-item-already-claimed-by-you'))
        return
      }
      if (claim === 'claimed-by-someone-else') {
        toast.warning(t('deposit-item-already-claimed'))
        return
      }
    }

    setLastScannedItem(item)
  }

  useEffect(() => {
    if (!lastScannedItem) return
    const alreadyExist = scannedItems.find(q => q.id === lastScannedItem.id)
    if (alreadyExist) {
      toast.warning(t('item-aready-added'))
      return
    }

    let tempItems

    switch (lastScannedItem.status) {
      case 'deposit':
        toast.success(`${lastScannedItem.label} ${t('added-successfully')}`)
        tempItems = categorizedDepositItems
        tempItems.depositItems.push(lastScannedItem)
        setCategorizedDepositItems(tempItems)
        break
      case 'assigned':
        tempItems = categorizedDepositItems
        tempItems.assignedItems.push(lastScannedItem)
        setCategorizedDepositItems(tempItems)
        toast.warning(`${t('deposit-item-assigned-to-someone')}`)
        break
      default:
        tempItems = categorizedDepositItems
        tempItems.otherItems.push(lastScannedItem)
        setCategorizedDepositItems(tempItems)
        toast.warning(`${t('item-not-deposit')}`)
        break
    }

    addToList(lastScannedItem)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastScannedItem])

  const addToList = async (data: any) => {
    let index = itemListByType.findIndex(r => r._id === data.itemTypeId)
    if (index < 0) {
      setItemListByType(itemListByType => {
        return [...itemListByType, { _id: data.itemTypeId, itemTypeIcon: data.iconUrl, items: [data] }]
      })
      setScannedItems(scannedItems => {
        return [...scannedItems, data]
      })
    } else {
      let itemIndex = itemListByType[index].items.findIndex(i => i.id === data._id)
      if (itemIndex < 0) {
        const tempItemList = itemListByType
        tempItemList[index].items.push(data)
        setItemListByType(tempItemList)
        setScannedItems(scannedItems => [...scannedItems, data])
      } else {
        toast.error(t('item-aready-added'))
      }
    }
  }

  useEffect(() => {
    const onErrors = () => {
      if (ended) {
        if (errors.length > 0) {
          errors.forEach(error => {
            toast.error(t(error.msg, { ns: 'errors' }))
          })
        }
      }
    }
    return () => {
      onErrors()
      setEnded(false)
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ended])

  useEffect(() => {
    if (scannedItems.length >= currentCapacity) {
      setIsFull(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannedItems, dropoff])

  useEffect(() => {
    async function onLoad() {
      if (dropoff) setIsFull(dropoff.freeCapacity > 0 ? false : true)
    }
    onLoad()
  }, [dropoff])

  const onCancel = () => {
    history.push('/')
  }

  const onClickScanner = () => {
    setActiveTab(1)
    setScannerActive(true)
  }

  const onClickList = () => {
    setActiveTab(2)
    setScannerActive(false)
  }

  const onItemTypeClick = (items: ItemToAssignData[]) => {
    if (items.length > 0) {
      setItemTypeExpanded(items)
    }
  }

  const onScanToRemove = () => {
    setShowRemoveScanner(true)
  }

  const onScanToRemoveQRCode = async (code: string) => {
    const uuid = parseItemQrCode(code)
    if (!uuid) {
      toast.error(t('invalid-item-qr-code'))
      return
    }
    const index = scannedItems.findIndex(r => r.uuid === uuid)
    if (index < 0) {
      toast.error(t('item-not-added'))
      return
    }

    let tempType = ''

    setScannedItems(
      scannedItems.filter(item => {
        if (item.uuid === uuid) tempType = item.label
        return item.uuid !== uuid
      }),
    )

    let tempItemListByType: ItemListData[] = []

    for (const type of itemListByType) {
      const y = type.items.filter(item => item.uuid !== uuid)
      tempItemListByType.push({ _id: type._id, itemTypeIcon: type.itemTypeIcon, items: y })
    }

    setCurrentCapacity(currentCapacity + 1)

    toast.info(`${tempType} ${t('removed-from-list')}`)
    setItemListByType(tempItemListByType)
  }

  const onClickToRemove = async (uuid: string) => {
    let tempType = ''

    setScannedItems(
      scannedItems.filter(item => {
        if (item.uuid === uuid) tempType = item.label
        return item.uuid !== uuid
      }),
    )

    setItemTypeExpanded(itemTypeExpanded.filter(item => item.uuid !== uuid))

    let tempItemListByType: ItemListData[] = []

    for (const type of itemListByType) {
      const y = type.items.filter(item => item.uuid !== uuid)
      tempItemListByType.push({ _id: type._id, itemTypeIcon: type.itemTypeIcon, items: y })
    }

    setCurrentCapacity(currentCapacity + 1)

    toast.info(`${tempType} ${t('removed-from-list')}`)
    setItemListByType(tempItemListByType)
  }

  const onScanToRemoveCancecl = () => {
    setShowRemoveScanner(false)
  }

  useEffect(() => {
    const onLoad = async () => {
      if (scannedItems.length < 1) {
        setActiveTab(1)
        setScannerActive(true)
      }
    }
    onLoad()
  }, [scannedItems])

  const onFinishScanning = () => {
    setShowReturnOverview(true)
  }

  const onConfirmAndSubmit = async () => {
    setIsLoading(true)
    if (!!!dropoff?._id || scannedItems.length < 1) {
      toast.error(t('unknown-error-occured'))
      setIsLoading(false)
      return
    }

    let itemIds: string = ''
    scannedItems.forEach(item => {
      itemIds += `"${item.id}",`
    })

    const mutation = `mutation {
      completeDeposit(input: {
          id:"${depositId}",
          itemIds: [${itemIds}]
          returnSiteId: "${dropoff?._id}"
      })
      {
          supportCode
      }
  }`

    const response = await wrappedExtendedMutate(mutation)

    if (!response) {
      setEnded(true)
      history.replace('/deposit/fail', { supportCode: supportCode })
      return
    }

    history.replace('/deposit/success')
  }

  return (
    <>
      {isLoading ? (
        <SplashScreen withFooter={false} />
      ) : showReturnOverview ? (
        <main className="mainHome">
          <div className="screen-header">
            <Link to="#" replace className="back" onClick={() => setShowReturnOverview(false)}>
              <ArrowLeftIcon className="bo-back" />
              {t('back')}
            </Link>
            <h1>{t('return-overview')}</h1>
          </div>
          <div className="items-overview">
            <div className="items-overview-header">
              <label>{t('item')}</label>
              <label>{t('quantity')}</label>
            </div>
            <div className="items-overview-listed">
              {itemListByType.map(type => (
                <div key={type._id} className="items-overview-item-type">
                  <div className="items-overview-item-type-info">
                    <label>{t(type.items[0].label)}</label>
                    <label>{type.items.length}</label>
                  </div>
                  <div className="custom-divider"></div>
                </div>
              ))}
            </div>
            <div className="items-overview-totals">
              <label>{t('orders.total')}</label>
              <label>{scannedItems.length}</label>
            </div>
          </div>
          <div className="support-code-container">
            <div className="support-code-header">
              <label>{t('your-support-code')}</label>
            </div>
            <div className="support-code-content">
              <div>
                <label>{supportCode}</label>
              </div>
              <div>
                <label>{t('support-code-info')}</label>
              </div>
            </div>
          </div>
          <div className="buttonDock-150"></div>
          <div className="floating-buttons">
            <button className="button-medium main-button" onClick={onConfirmAndSubmit}>
              {t('confirm-and-submit')}
            </button>
            <button className="button-medium naked-button" onClick={onCancel}>
              {t('cancel')}
            </button>
          </div>
        </main>
      ) : showRemoveScanner ? (
        <section className="qrScanner qr-scanner-expanded">
          <QrScannerExpanded callback={onScanToRemoveQRCode} callbackCancel={onScanToRemoveCancecl} heading={t('scan-remove')} text={t('scan-remove-text')} cancelBtnText={t('finish-removing')} cancelBtnClassName="circled-naked-button" />
        </section>
      ) : itemTypeExpanded.length > 0 ? (
        <main className="mainHome">
          <div className="screen-header">
            <Link to="#" replace className="back" onClick={() => setItemTypeExpanded([])}>
              <ArrowLeftIcon className="bo-back" />
              {t('back')}
            </Link>
            <div className="item-type-info">
              <img className="item-icon" alt="item-icon" src={itemTypeExpanded[0].iconUrl || t('no-image-available')} />
              <label>{itemTypeExpanded[0].label}</label>
            </div>
          </div>
          <div className="scanned-items-container">
            <label>
              {itemTypeExpanded.length} {t('scanned-items')}
            </label>
            {itemTypeExpanded.map(item => (
              <div key={item.uuid} className="scanned-item-label">
                {item.serialNumber && (
                  <>
                    <div className="label-with-bin">
                      <label>{t('serial-number')}</label>
                      <BinIcon onClick={() => onClickToRemove(item.uuid)} />
                    </div>
                    <label>{item.serialNumber}</label>
                  </>
                )}
                <div className="custom-divider"></div>
                {item.status === 'deposit' ? <div className="item-refund-status refundable">{t('refundable')}</div> : <div className="item-refund-status">{t('non-refundable')}</div>}
              </div>
            ))}
          </div>
        </main>
      ) : (
        <>
          {scannedItems.length < 1 ? (
            <></>
          ) : (
            <section className={`custom-tab-header ${scannerActive ? 'custom-tab-scanner-active' : ''}`}>
              <div className={`custom-tab ${activeTab === 1 ? 'active' : ''}`} onClick={onClickScanner}>
                <ScanIcon />
                <label>{t('scanner')}</label>
              </div>
              <div className={`custom-tab ${activeTab === 2 ? 'active' : ''}`} onClick={onClickList}>
                <ListIcon />
                <label>
                  {t('scanned-items')} ({scannedItems.length})
                </label>
              </div>
            </section>
          )}
          {activeTab === 1 && (
            <div key={isScanning} className={`custom-tab-panel ${activeTab === 1 && 'active-tab-panel'}`}>
              <section className="qrScanner qr-scanner-expanded">
                <QrScannerExpanded
                  callback={onQrCode}
                  callbackCancel={onCancel}
                  onContinueBtn={onFinishScanning}
                  heading={t('scan-containers')}
                  text={t('scan-containers-text')}
                  showContinueBtn={true}
                  continueBtnText={t('finish-scanning')}
                  className="tabbed-scanner"
                  continueBtnDisabled={scannedItems.length > 0 ? false : true}
                />
              </section>
            </div>
          )}
          {activeTab === 2 && (
            <div className={`custom-tab-panel ${activeTab === 2 && 'active-tab-panel'}`}>
              {lastScannedItem && (
                <div className="last-scanned-item">
                  <label>{t('last-scanned')}</label>
                  <div className="last-scanned-info-container">
                    <div className="last-scanned-item-type-info">
                      <img className="item-icon" alt="item-icon" src={lastScannedItem.iconUrl || t('no-image-available')} />
                      <label>{lastScannedItem.label}</label>
                    </div>
                    <div className="last-scanned-info">
                      <div className="last-scanned-info-item">
                        {lastScannedItem.serialNumber && (
                          <>
                            <label>{t('serial-number')}</label>
                            <label>{lastScannedItem.serialNumber}</label>
                          </>
                        )}
                      </div>
                      {lastScannedItem.merchant && (
                        <div className="last-scanned-info-item">
                          <label>{t('borrowed-from')}</label>
                          <label>{lastScannedItem.merchant}</label>
                        </div>
                      )}
                      {lastScannedItem.dueDate && (
                        <div className="last-scanned-info-item">
                          <label>{t('due')}</label>
                          <label>{moment.utc(lastScannedItem.dueDate).locale(lang).format('MMM Do YYYY')}</label>
                        </div>
                      )}
                      {lastScannedItem.status === 'deposit' ? (
                        <div className="last-scanned-refund-status refundable right-aligned">{t('refundable')}</div>
                      ) : (
                        <div className="last-scanned-refund-status right-aligned">{t('non-refundable')}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="deposit-item-list">
                <div className="deposit-item-list-header">
                  <label>{t('item-list')}</label>
                  <label className="scan-to-remove" onClick={onScanToRemove}>
                    <BinIcon />
                    {t('scan-remove')}
                  </label>
                </div>
                <div className="items-categorized">
                  {itemListByType &&
                    itemListByType.length > 0 &&
                    itemListByType.map(
                      (type, index) =>
                        type.items &&
                        type.items.length > 0 && (
                          <div key={type.items[0].label}>
                            <div className="categorized-item" onClick={() => onItemTypeClick(type.items)}>
                              <div className="categorized-item-info">
                                <div className="categorized-item-type-info">
                                  <img className="item-icon" alt="item-icon" src={type.itemTypeIcon || t('no-image-available')} />
                                  <label>{type.items[0].label}</label>
                                </div>
                                <div className="item-type-count">
                                  <label>({type.items.length})</label>
                                  <ArrowRightIcon />
                                </div>
                              </div>
                            </div>
                            <div className="custom-divider"></div>
                          </div>
                        ),
                    )}
                </div>
              </div>
              <div className="buttonDock-150"></div>
              <div className="floating-buttons">
                <button className="button-medium main-button" onClick={onFinishScanning}>
                  {t('finish-scanning')}
                </button>
                <button className="button-medium naked-button" onClick={onCancel}>
                  {t('cancel')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}

export { DepositItems as default }
